import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { Tenant } from "../../api/client.ts";

export interface TenantProfileValues {
  offering_text: string;
  icp_text: string;
  buyer_persona_text: string;
}

export const MAX_OFFERING_SENTENCES = 7;

/** The profile of a tenant, or of a prospect order (it holds the same three fields). */
export function profileValues(
  source: Pick<Tenant, "offering_text" | "icp_text" | "buyer_persona_text"> | undefined | null,
): TenantProfileValues {
  return {
    offering_text: source?.offering_text ?? "",
    icp_text: source?.icp_text ?? "",
    buyer_persona_text: source?.buyer_persona_text ?? "",
  };
}

/** The values as the profile endpoint takes them: blanks clear a field. */
export function profilePayload(values: TenantProfileValues) {
  return {
    offering_text: values.offering_text.trim() || null,
    icp_text: values.icp_text.trim() || null,
    buyer_persona_text: values.buyer_persona_text.trim() || null,
  };
}

export function sentenceCount(text: string): number {
  return (text.match(/[.!?](\s|$)/g) ?? []).length;
}

interface TenantProfileFieldsProps {
  values: TenantProfileValues;
  onChange: (values: TenantProfileValues) => void;
  disabled?: boolean;
  /** Rows of the ICP and persona boxes. */
  minRows?: number;
}

/** What the company sells, its ICP and its ideal buyer persona: what agents work from. */
export default function TenantProfileFields({
  values,
  onChange,
  disabled,
  minRows = 4,
}: TenantProfileFieldsProps) {
  const sentences = sentenceCount(values.offering_text);
  const tooLong = sentences > MAX_OFFERING_SENTENCES;
  const set = (key: keyof TenantProfileValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...values, [key]: e.target.value });

  return (
    <Stack spacing={2.5}>
      <TextField
        label="What does the company sell?"
        multiline
        fullWidth
        minRows={3}
        disabled={disabled}
        value={values.offering_text}
        onChange={set("offering_text")}
        error={tooLong}
        helperText={
          tooLong
            ? `${sentences} sentences — keep it to ${MAX_OFFERING_SENTENCES} at most.`
            : "Brief: what it is, the problem it solves and for whom. 5–7 sentences at most."
        }
        slotProps={{ htmlInput: { maxLength: 1500 } }}
      />
      <TextField
        label="Ideal customer profile"
        multiline
        fullWidth
        minRows={minRows}
        disabled={disabled}
        value={values.icp_text}
        onChange={set("icp_text")}
        helperText="Industries, company size, geography and signs a company needs this. Markdown."
      />
      <TextField
        label="Ideal buyer persona"
        multiline
        fullWidth
        minRows={minRows}
        disabled={disabled}
        value={values.buyer_persona_text}
        onChange={set("buyer_persona_text")}
        helperText="The people who buy or champion it: titles, seniority, department, what they care about."
      />
    </Stack>
  );
}
